import React, { useState } from 'react'
import { theme } from '../../../core/design-system/theme'

const prefersReducedMotion =
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

function ForkKnifeIcon() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Fork */}
      <line x1="18" y1="10" x2="18" y2="32" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="14" y1="10" x2="14" y2="22" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="22" y1="10" x2="22" y2="22" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <path d="M14 22 Q18 26 22 22" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" fill="none" />
      <line x1="18" y1="32" x2="18" y2="54" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      {/* Knife */}
      <path d="M46 10 Q50 14 50 24 L46 28 L46 54" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <line x1="46" y1="10" x2="46" y2="28" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

function HeartHandsIcon() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Heart */}
      <path
        d="M32 44 C32 44 14 33 14 22 C14 17 17.5 13 22 13 C25.5 13 28.5 15 32 18 C35.5 15 38.5 13 42 13 C46.5 13 50 17 50 22 C50 33 32 44 32 44Z"
        stroke={theme.colors.terracotta}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill={theme.colors.terracottaLight}
        fillOpacity="0.3"
      />
      {/* Left hand */}
      <path
        d="M10 46 Q8 42 10 38 L14 34 Q18 34 18 38 L20 44"
        stroke={theme.colors.terracotta}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Right hand */}
      <path
        d="M54 46 Q56 42 54 38 L50 34 Q46 34 46 38 L44 44"
        stroke={theme.colors.terracotta}
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
      {/* Hands cradling */}
      <path
        d="M10 46 Q32 56 54 46"
        stroke={theme.colors.terracotta}
        strokeWidth="2.5"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg
      width="64"
      height="64"
      viewBox="0 0 64 64"
      fill="none"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Sun circle */}
      <circle cx="32" cy="32" r="10" stroke={theme.colors.terracotta} strokeWidth="2.5" fill={theme.colors.terracottaLight} fillOpacity="0.3" />
      {/* Rays */}
      <line x1="32" y1="10" x2="32" y2="16" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="32" y1="48" x2="32" y2="54" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="10" y1="32" x2="16" y2="32" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="48" y1="32" x2="54" y2="32" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="17" y1="17" x2="21" y2="21" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="43" y1="43" x2="47" y2="47" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="47" y1="17" x2="43" y2="21" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="21" y1="43" x2="17" y2="47" stroke={theme.colors.terracotta} strokeWidth="2.5" strokeLinecap="round" />
    </svg>
  )
}

const PAGES = [
  {
    icon: <ForkKnifeIcon />,
    headline: 'Going out with a baby is hard.',
    body: 'We help create your village — so you can actually enjoy your meal.',
  },
  {
    icon: <HeartHandsIcon />,
    headline: 'A trusted helper joins you at the restaurant.',
    body: 'They hold, entertain, or care for your baby while you eat.',
  },
  {
    icon: <SunIcon />,
    headline: 'Because parents deserve to feel human again.',
    body: 'Sit down. Connect. Breathe. Someone is there to help.',
  },
]

const OUR_STORY_PARAGRAPHS = [
  'One night, we went out to dinner with our baby — like so many parents do, hoping to enjoy a meal but expecting the usual juggling act.',
  'Then something unexpected happened.',
  'A friend\'s mom, visiting from Italy, gently took our baby and held them while we ate. No rush. No stress. Just a moment to sit, talk, and feel like ourselves again.',
  'And in that moment, it clicked: This is what people mean when they say "it takes a village." But so many of us don\'t have that village nearby. So we built this — to bring that feeling back. A way for parents to go out, connect, and breathe — knowing someone is there to help, even for just a little while.',
]

function OurStoryModal({ onClose }) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Our Story"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        zIndex: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        style={{
          background: theme.colors.card,
          borderRadius: 18,
          maxWidth: 480,
          width: '100%',
          padding: '28px',
          position: 'relative',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <button
          autoFocus
          onClick={onClose}
          aria-label="Close Our Story"
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 4,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: theme.colors.textSecondary,
            borderRadius: 8,
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = theme.colors.textPrimary }}
          onMouseLeave={(e) => { e.currentTarget.style.color = theme.colors.textSecondary }}
          onFocus={(e) => { e.currentTarget.style.outline = `2px solid ${theme.colors.terracottaDark}`; e.currentTarget.style.outlineOffset = '2px' }}
          onBlur={(e) => { e.currentTarget.style.outline = 'none' }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <h2
          style={{
            fontFamily: theme.fonts.display,
            fontSize: 24,
            fontWeight: 600,
            color: theme.colors.textPrimary,
            margin: '0 0 16px',
          }}
        >
          Our Story
        </h2>

        {OUR_STORY_PARAGRAPHS.map((text, i) => (
          <p
            key={i}
            style={{
              fontFamily: theme.fonts.body,
              fontSize: 15,
              color: theme.colors.textSecondary,
              lineHeight: 1.7,
              margin: i < OUR_STORY_PARAGRAPHS.length - 1 ? '0 0 12px' : 0,
            }}
          >
            {text}
          </p>
        ))}
      </div>
    </div>
  )
}

export default function OnboardingView({ onComplete }) {
  const [currentPage, setCurrentPage] = useState(0)
  const [showStory, setShowStory] = useState(false)
  const [skipHovered, setSkipHovered] = useState(false)
  const [btnHovered, setBtnHovered] = useState(false)
  const [storyLinkHovered, setStoryLinkHovered] = useState(false)

  function handleNext() {
    setCurrentPage((p) => p + 1)
  }

  return (
    <main
      role="main"
      style={{
        minHeight: '100vh',
        background: theme.colors.cream,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        fontFamily: theme.fonts.body,
        overflow: 'hidden',
      }}
    >
      {/* Skip button */}
      <button
        aria-label="Skip onboarding"
        onClick={onComplete}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: '16px',
          fontFamily: theme.fonts.body,
          fontSize: 14,
          color: skipHovered ? theme.colors.textPrimary : theme.colors.textSecondary,
          zIndex: 10,
        }}
        onMouseEnter={() => setSkipHovered(true)}
        onMouseLeave={() => setSkipHovered(false)}
        onFocus={(e) => { e.currentTarget.style.outline = `2px solid ${theme.colors.terracottaDark}`; e.currentTarget.style.outlineOffset = '2px' }}
        onBlur={(e) => { e.currentTarget.style.outline = 'none' }}
      >
        Skip
      </button>

      {/* Slide container */}
      <div
        style={{
          flex: 1,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
        aria-label={`Page ${currentPage + 1} of 3`}
      >
        <div
          style={{
            display: 'flex',
            flex: 1,
            transform: `translateX(-${currentPage * 100}%)`,
            transition: prefersReducedMotion ? 'none' : 'transform 0.35s ease',
            willChange: 'transform',
          }}
        >
          {PAGES.map((page, index) => (
            <div
              key={index}
              style={{
                minWidth: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '80px 32px 32px',
                boxSizing: 'border-box',
              }}
            >
              <div style={{ width: 64, height: 64, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {page.icon}
              </div>
              <h1
                style={{
                  fontFamily: theme.fonts.display,
                  fontSize: 26,
                  fontWeight: 600,
                  color: theme.colors.textPrimary,
                  textAlign: 'center',
                  marginTop: 20,
                  marginBottom: 0,
                  lineHeight: 1.3,
                }}
              >
                {page.headline}
              </h1>
              <p
                style={{
                  fontFamily: theme.fonts.body,
                  fontSize: 16,
                  color: theme.colors.textSecondary,
                  textAlign: 'center',
                  lineHeight: 1.7,
                  marginTop: 12,
                  marginBottom: 0,
                  maxWidth: 320,
                }}
              >
                {page.body}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom controls */}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '0 32px 48px',
          gap: 16,
        }}
      >
        {/* Dot indicators */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }} aria-hidden="true">
          {PAGES.map((_, i) => (
            <div
              key={i}
              style={{
                width: i === currentPage ? 20 : 8,
                height: 8,
                borderRadius: 4,
                background: i === currentPage ? theme.colors.terracotta : theme.colors.border,
                transition: prefersReducedMotion ? 'none' : 'width 0.25s ease, background 0.25s ease',
              }}
            />
          ))}
        </div>

        {currentPage < 2 ? (
          <button
            onClick={handleNext}
            style={{
              background: btnHovered ? theme.colors.terracottaDark : theme.colors.terracotta,
              color: '#FFFFFF',
              borderRadius: theme.layout.buttonRadius,
              padding: '14px 32px',
              fontFamily: theme.fonts.body,
              fontSize: 15,
              fontWeight: 600,
              border: 'none',
              cursor: 'pointer',
              transition: prefersReducedMotion ? 'none' : 'background 0.15s ease',
              minWidth: 180,
            }}
            onMouseEnter={() => setBtnHovered(true)}
            onMouseLeave={() => setBtnHovered(false)}
            onFocus={(e) => { e.currentTarget.style.outline = `2px solid ${theme.colors.terracottaDark}`; e.currentTarget.style.outlineOffset = '2px' }}
            onBlur={(e) => { e.currentTarget.style.outline = 'none' }}
          >
            Next →
          </button>
        ) : (
          <>
            <button
              onClick={onComplete}
              aria-label="Find my village and book support"
              style={{
                background: btnHovered ? theme.colors.terracottaDark : theme.colors.terracotta,
                color: '#FFFFFF',
                borderRadius: theme.layout.buttonRadius,
                padding: '14px 32px',
                fontFamily: theme.fonts.body,
                fontSize: 15,
                fontWeight: 600,
                border: 'none',
                cursor: 'pointer',
                transition: prefersReducedMotion ? 'none' : 'background 0.15s ease',
                minWidth: 220,
              }}
              onMouseEnter={() => setBtnHovered(true)}
              onMouseLeave={() => setBtnHovered(false)}
              onFocus={(e) => { e.currentTarget.style.outline = `2px solid ${theme.colors.terracottaDark}`; e.currentTarget.style.outlineOffset = '2px' }}
              onBlur={(e) => { e.currentTarget.style.outline = 'none' }}
            >
              Find my village
            </button>
            <button
              onClick={() => setShowStory(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontFamily: theme.fonts.body,
                fontSize: 14,
                color: theme.colors.textSecondary,
                textDecoration: storyLinkHovered ? 'underline' : 'none',
                padding: '4px 8px',
              }}
              onMouseEnter={() => setStoryLinkHovered(true)}
              onMouseLeave={() => setStoryLinkHovered(false)}
              onFocus={(e) => { e.currentTarget.style.outline = `2px solid ${theme.colors.terracottaDark}`; e.currentTarget.style.outlineOffset = '2px' }}
              onBlur={(e) => { e.currentTarget.style.outline = 'none' }}
            >
              Why we built this →
            </button>
          </>
        )}
      </div>

      {showStory && <OurStoryModal onClose={() => setShowStory(false)} />}
    </main>
  )
}
